import {Button, Drawer, Form, Space} from 'antd';
import React, {useEffect} from 'react';
import {
  useCreateGroup,
  useCreateVariable,
  useUpdateGroup,
  useUpdateVariable,
} from '../../../hooks/parameters';
import {useTranslation} from '../../../hooks/useTranslation';
import AssessmentVariableForm from './AssessmentVariableForm';
import {getDisplayText} from './helpers';

interface AssessmentVariableDrawerProps {
  open: boolean;
  type: 'group' | 'variable';
  record: any | null;
  parentGroupId: string | number | null;
  onClose: () => void;
}

const AssessmentVariableDrawer: React.FC<AssessmentVariableDrawerProps> = ({
  open,
  type,
  record,
  parentGroupId,
  onClose,
}) => {
  const {t} = useTranslation();
  const [form] = Form.useForm();
  const createGroup = useCreateGroup();
  const updateGroup = useUpdateGroup();
  const createVariable = useCreateVariable();
  const updateVariable = useUpdateVariable();

  const isEdit = record != null;

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (record) {
      form.setFieldsValue({name: getDisplayText(record)});
    } else {
      form.setFieldsValue({name: ''});
    }
  }, [open, record, form]);

  const handleSubmit = async () => {
    try {
      const row = await form.validateFields();
      const payload: any = {name: row.name};

      if (type === 'group') {
        if (isEdit) {
          await updateGroup.mutateAsync({id: record.idVariableGroup, data: payload});
        } else {
          await createGroup.mutateAsync(payload);
        }
      } else {
        if (isEdit) {
          await updateVariable.mutateAsync({id: record.idVariable, data: payload});
        } else {
          await createVariable.mutateAsync({...payload, idVariableGroup: parentGroupId});
        }
      }
      onClose();
    } catch (err) {
      console.error('Validation Failed:', err);
    }
  };

  const titleKey =
    isEdit
      ? type === 'group'
        ? 'settings.content.impactAssessment.tree.actions.editGroup'
        : 'settings.content.impactAssessment.tree.actions.editVariable'
      : type === 'group'
        ? 'settings.content.impactAssessment.tree.actions.addGroup'
        : 'settings.content.impactAssessment.tree.actions.addVariable';

  return (
    <Drawer
      title={t(titleKey)}
      open={open}
      onClose={onClose}
      size="default"
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>
            {t('settings.content.impactAssessment.formulaTable.actions.cancel')}
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={
              createGroup.isPending ||
              updateGroup.isPending ||
              createVariable.isPending ||
              updateVariable.isPending
            }
          >
            {t('settings.content.impactAssessment.formulaTable.actions.save')}
          </Button>
        </Space>
      }
    >
      <AssessmentVariableForm form={form} type={type}/>
    </Drawer>
  );
};

export default AssessmentVariableDrawer;
